import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import type { EngagementEntry, EngagementRole } from "@/hooks/use-resource-engagements";
import { useResourceEngagements } from "@/hooks/use-resource-engagements";
import { constraintCellBackground } from "@/lib/constraint-colors";
import { positionLabel, positionTitle } from "@/lib/position-label";

const MONTH_ABBR = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Primary role badge colours — same tokens as the Pod slot colours used on the
// Solution Detail drawer and heatmap (--role-r1..r4). "Supporting" has no Pod
// slot, so it renders as a neutral/muted chip rather than inventing a fifth hue.
const ROLE_BADGE_COLOR: Record<Exclude<EngagementRole, "Supporting">, string> = {
  R1: "var(--role-r1)",
  R2: "var(--role-r2)",
  R3: "var(--role-r3)",
  R4: "var(--role-r4)",
};

// Engagement Standard Tier badge classes — verbatim copy of the TierTag map used
// on the Solutions list (src/routes/dashboard/index.tsx) and Solution Detail
// drawer, so tier colour stays consistent everywhere it's shown.
const TIER_CLASS: Record<string, string> = {
  Support: "bg-info-100 text-info-700",
  Growth: "bg-brand-50 text-brand-900",
  Standard: "bg-brand-100 text-brand-700",
  Strategic: "bg-brand-200 text-brand-900",
};

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function toTime(iso: string | null): number {
  return iso ? new Date(iso).getTime() : 0;
}

/** "Feb 2026 – present · 20 wks" (active) or "Aug – Dec 2025 · 18 wks" (ended). */
function formatEngagementRange(effectiveStart: string, effectiveEnd: string | null): string {
  const start = new Date(effectiveStart);
  const end = effectiveEnd ? new Date(effectiveEnd) : new Date();
  const weeks = Math.max(0, Math.round((end.getTime() - start.getTime()) / WEEK_MS));
  const startLabel = `${MONTH_ABBR[start.getMonth()] ?? ""} ${start.getFullYear()}`;

  if (!effectiveEnd) {
    return `${startLabel} – present · ${weeks} wks`;
  }

  const sameYear = start.getFullYear() === end.getFullYear();
  const range = sameYear
    ? `${MONTH_ABBR[start.getMonth()] ?? ""} – ${MONTH_ABBR[end.getMonth()] ?? ""} ${end.getFullYear()}`
    : `${startLabel} – ${MONTH_ABBR[end.getMonth()] ?? ""} ${end.getFullYear()}`;
  return `${range} · ${weeks} wks`;
}

function formatHours(hours: number): string {
  return `${hours.toLocaleString()} h`;
}

function RoleBadge({
  role,
  seatDiscipline,
}: {
  role: EngagementRole;
  seatDiscipline?: { code: string; name: string };
}) {
  if (role === "Supporting") {
    return (
      <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-xs font-bold text-muted-foreground">
        Supporting
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-bold text-white"
      style={{ background: ROLE_BADGE_COLOR[role] }}
      title={positionTitle(role, seatDiscipline ? { [role]: seatDiscipline } : null)}
    >
      {positionLabel(role, seatDiscipline ? { [role]: seatDiscipline } : null)}
    </span>
  );
}

function TierBadge({ tier }: { tier: string }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-xs font-medium ${
        TIER_CLASS[tier] ?? "bg-muted text-foreground"
      }`}
    >
      {tier}
    </span>
  );
}

/**
 * Constraint chip row for one Engagement. These chips carry a group hue but no
 * RM-confirmed intensity (that dimension lives on the Resource's own Constraint
 * Ratings, not on the Assignment), so we render at a fixed representative depth
 * — reusing the exact constraintGroupHue/constraintCellBackground helpers the
 * Constraint Ratings section above uses, rather than a new colour system.
 */
function ConstraintChips({ constraints }: { constraints: EngagementEntry["constraints"] }) {
  if (constraints.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {constraints.map((c) => (
        <span
          key={`${c.group}-${c.name}`}
          title={`${c.group} · ${c.name}`}
          className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium text-white"
          style={{ background: constraintCellBackground(c.group, 4) }}
        >
          {c.name}
        </span>
      ))}
    </div>
  );
}

function EngagementCard({ entry }: { entry: EngagementEntry }) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <span className="mt-1 text-xs font-medium text-muted-foreground">
          {formatEngagementRange(entry.effectiveStart, entry.effectiveEnd)}
        </span>
        <span
          className="shrink-0 font-display text-xl font-bold leading-none text-foreground"
          title="Utilization — hours logged, not capacity"
        >
          {entry.hoursLogged.toLocaleString()}
          <span className="ml-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            hrs
          </span>
        </span>
      </div>
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <Link
          to="/dashboard"
          search={{ solutionId: entry.solutionId }}
          className="truncate font-display text-sm font-bold leading-tight text-foreground hover:underline"
        >
          {entry.solutionName}
        </Link>
        <RoleBadge role={entry.role} seatDiscipline={entry.seatDiscipline} />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <span className="truncate">{entry.accountName}</span>
        <TierBadge tier={entry.engagementStandardTier} />
      </div>
      <ConstraintChips constraints={entry.constraints} />
    </div>
  );
}

function EngagementGroup({ title, entries }: { title: string; entries: EngagementEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <div className="mb-3">
      <div className="mb-1.5 flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </span>
        <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-semibold text-muted-foreground">
          {entries.length}
        </span>
      </div>
      <div className="space-y-2">
        {entries.map((e) => (
          <EngagementCard key={e.assignmentId} entry={e} />
        ))}
      </div>
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-card p-3 text-center">
      <div className="font-display text-xl font-bold leading-none">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

/**
 * Engagements — read-only Assignment history for a Resource (§6.1.4 "View
 * Assignment History"). Sixth stacked section on the Resource Detail drawer,
 * grouped into Active now / Previous · completed. No filter UI yet — deferred
 * to a follow-up story per the approved plan.
 */
export function EngagementsTimeline({
  resourceId,
  enabled = true,
}: {
  resourceId: string;
  enabled?: boolean;
}) {
  const { data, isLoading, isError, refetch } = useResourceEngagements(resourceId, { enabled });

  const engagements = data?.engagements ?? [];
  const active = useMemo(
    () =>
      engagements
        .filter((e) => e.effectiveEnd === null)
        .sort((a, b) => toTime(b.effectiveStart) - toTime(a.effectiveStart)),
    [engagements],
  );
  const previous = useMemo(
    () =>
      engagements
        .filter((e) => e.effectiveEnd !== null)
        .sort((a, b) => toTime(b.effectiveEnd) - toTime(a.effectiveEnd)),
    [engagements],
  );

  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Engagements
        </div>
        <span className="text-xs text-muted-foreground">Assignment history</span>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {!isLoading && isError && (
        <ErrorState
          message="Couldn't load Engagements."
          onRetry={() => refetch()}
          className="py-6"
        />
      )}

      {!isLoading && !isError && engagements.length === 0 && (
        <div className="rounded-md border border-dashed border-border bg-card/50 px-3 py-6 text-center text-sm text-muted-foreground">
          No Assignments yet
        </div>
      )}

      {!isLoading && !isError && data && engagements.length > 0 && (
        <>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <SummaryTile label="Solutions staffed" value={String(data.solutionsStaffedCount)} />
            <SummaryTile label="Hours logged" value={formatHours(data.totalHoursLogged)} />
          </div>
          <EngagementGroup title="Active now" entries={active} />
          <EngagementGroup title="Previous · completed" entries={previous} />
        </>
      )}
    </section>
  );
}
